import { useCallback, useMemo } from 'react';
import chest from '~/assets/icons/chest.svg';
import star from '~/assets/icons/four_final_star.svg';
import { CollectionImage } from '~/components/collections';
import { ClothesImage } from '~/components/collections/ClothesImage';
import {
	Button,
	ButtonBorderRadius,
	ButtonColors,
	ButtonHeight,
} from '~/components/ui/Button';
import { ImgIcon } from '~/components/ui/ImgIcon';
import { Text } from '~/components/ui/Text';
import { rage, useObservable } from '~/rage';
import {
	isClothesItem,
	ModalButtonColor,
	ModalType,
	ParseRewardValueTypeMap,
	RewardType,
	WalletType,
} from '~/shared';
import cardSoft from '../../assets/images/cases/card_soft.png';
import {
	setActiveCase,
	setIsOpeningCase,
	setShopOpenCaseVisible,
	setShopPlayerItemsOpen,
	useLayoutDispatch,
} from '../../redux';
import {
	StyledBottomContainer,
	StyledBottomShadow,
	StyledButtonContent,
	StyledButtonGrid,
	StyledContainer,
	StyledCount,
	StyledExpContainer,
	StyledImageContainer,
	StyledShadow,
} from './ShopPlayerItem.styled';

const ShopPlayerItem = ({
	type,
	name,
	id,
	count,
	imageId,
}: {
	type: RewardType;
	name: string;
	id: string;
	count?: number;
	imageId?: string;
}) => {
	const dispatch = useLayoutDispatch();
	const cases = useObservable(rage.cases.getLootboxes());
	const itemPrice = useMemo(() => rage.inventoryDonate.getPriceById(id), [id]);

	const displayId = imageId ?? id;

	const reward = rage.cases.getRewardById(id);
	const amount = useMemo(() => {
		if (type === RewardType.EXP) {
			return (reward.value as ParseRewardValueTypeMap[RewardType.EXP]) ?? 0;
		}
		if (type === RewardType.MONEY) {
			return (reward.value as ParseRewardValueTypeMap[RewardType.MONEY]) ?? 0;
		}

		return 0;
	}, [reward]);

	const handleSell = useCallback(async () => {
		if (itemPrice === null) {
			return;
		}

		rage.modal.showModal(
			{
				type: ModalType.Price,
				title: 'Подтверждение',
				text: `Вы действительно хотите продать “${name}”?`,
				price: itemPrice,
				wallet: WalletType.Donate,
				acceptButton: { text: 'Продать', color: ModalButtonColor.Success },
				cancelButton: {
					text: 'Отменить',
					color: ModalButtonColor.Secondary,
				},
			},
			(result) => {
				if (result.isAccepted) {
					rage.inventoryDonate.sellReward(id);
				}
			},
		);
	}, [itemPrice]);

	const handleOpenCase = () => {
		const activeCase = cases.find((caseItem) => caseItem.donateItemId === id);
		if (!activeCase) {
			return;
		}
		const { getClientConfig: _getClientConfig, ...caseWithoutConfig } =
			activeCase as any;
		dispatch(setActiveCase(caseWithoutConfig));
		dispatch(setShopPlayerItemsOpen(false));
		dispatch(setIsOpeningCase(false));
		dispatch(setShopOpenCaseVisible(true));
	};

	return (
		<StyledContainer>
			<StyledShadow />
			<StyledBottomShadow />
			<StyledImageContainer $type={type} $isClothes={isClothesItem(displayId)}>
				{type === RewardType.LOOTBOX && (
					<CollectionImage collection={'cases'} id={displayId} size={180} />
				)}
				{type === RewardType.VEHICLE && (
					<CollectionImage collection={'vehicles'} id={displayId} size={96} />
				)}
				{type === RewardType.ITEM && !isClothesItem(displayId) && (
					<CollectionImage collection={'items'} id={displayId} size={140} />
				)}
				{type === RewardType.ITEM && isClothesItem(displayId) && (
					<ClothesImage id={displayId} size={140} />
				)}
				{type === RewardType.MONEY && (
					<img src={cardSoft} width={140} height={140} />
				)}
				{type === RewardType.EXP && (
					<StyledExpContainer>
						<ImgIcon icon={star} width={30} height={30} color="#FFB500" />
						<Text>{amount.toLocaleString('ru-RU')}</Text>
					</StyledExpContainer>
				)}
			</StyledImageContainer>
			{count && count > 1 && (
				<StyledCount>
					<Text>{count} шт.</Text>
				</StyledCount>
			)}
			<StyledBottomContainer>
				<Text>{name}</Text>
				{type === RewardType.LOOTBOX && (
					<Button
						color={ButtonColors.Select}
						height={ButtonHeight.Height40}
						borderRadius={ButtonBorderRadius.Radius5}
						onClick={handleOpenCase}
					>
						<StyledButtonContent>
							<img src={chest} />
							<Text>Открыть кейс</Text>
						</StyledButtonContent>
					</Button>
				)}
				{type !== RewardType.LOOTBOX && (
					<StyledButtonGrid $canBeSold={itemPrice !== null}>
						{itemPrice !== null && (
							<Button
								color={ButtonColors.Buy}
								height={ButtonHeight.Height40}
								borderRadius={ButtonBorderRadius.Radius5}
								onClick={handleSell}
							>
								<StyledButtonContent>
									<img src={chest} />
									<Text>Продать</Text>
								</StyledButtonContent>
							</Button>
						)}
						<Button
							color={ButtonColors.Default}
							height={ButtonHeight.Height40}
							borderRadius={ButtonBorderRadius.Radius5}
							onClick={() => rage.inventoryDonate.useReward(id)}
						>
							<StyledButtonContent>
								<img src={chest} />
								<Text>Забрать</Text>
							</StyledButtonContent>
						</Button>
					</StyledButtonGrid>
				)}
			</StyledBottomContainer>
		</StyledContainer>
	);
};

export { ShopPlayerItem };
